import { useEffect, useState } from "react";
import { Play } from "lucide-react";
import { fetchFeaturedReel } from "../lib/api";
import { isDirectVideo } from "../lib/embed";
import FeaturedReelPlayer from "./FeaturedReelPlayer";

const DEFAULT_SECONDS = 10;

// Per-clip fallback duration when no metadata is available.
function fallbackSeconds(clip) {
    const n = Number(clip?.auto_advance_seconds);
    if (Number.isFinite(n) && n >= 3 && n <= 60) return n;
    return DEFAULT_SECONDS;
}

// Synchronous reel estimate (no async metadata probing).
function syncEstimate(clips) {
    return clips.reduce((acc, c) => acc + fallbackSeconds(c), 0);
}

// Format seconds as M:SS or MM:SS.
function formatDuration(totalSec) {
    const s = Math.max(0, Math.round(totalSec || 0));
    const m = Math.floor(s / 60);
    const ss = String(s % 60).padStart(2, "0");
    return `${m}:${ss}`;
}

// Probe an HTML5 video's duration via metadata; resolves to seconds or null.
function probeVideoDuration(url, timeoutMs = 4000) {
    return new Promise((resolve) => {
        if (!url) return resolve(null);
        const v = document.createElement("video");
        v.preload = "metadata";
        v.muted = true;
        let done = false;
        const finish = (val) => {
            if (done) return;
            done = true;
            try {
                v.removeAttribute("src");
                v.load();
            } catch {
                /* ignore */
            }
            resolve(val);
        };
        v.addEventListener("loadedmetadata", () => {
            const d = v.duration;
            finish(Number.isFinite(d) && d > 0 ? d : null);
        });
        v.addEventListener("error", () => finish(null));
        setTimeout(() => finish(null), timeoutMs);
        v.src = url;
    });
}

/**
 * Subtle "Play Reel" trigger powered by featured clips.
 * Hidden unless at least 2 featured published clips exist.
 * Label includes a quiet duration estimate (M:SS / MM:SS).
 */
export const PlayFeaturedReelButton = ({ className = "" }) => {
    const [clips, setClips] = useState([]);
    const [open, setOpen] = useState(false);
    const [seconds, setSeconds] = useState(0);

    useEffect(() => {
        fetchFeaturedReel()
            .then(setClips)
            .catch(() => setClips([]));
    }, []);

    // Reel duration estimate — synchronous fallback first, then refine with
    // real metadata for any direct-video clips.
    useEffect(() => {
        if (clips.length < 2) {
            setSeconds(0);
            return undefined;
        }
        setSeconds(syncEstimate(clips));
        let cancelled = false;
        (async () => {
            try {
                const probes = await Promise.all(
                    clips.map((c) =>
                        isDirectVideo(c.embed_url)
                            ? probeVideoDuration(c.embed_url)
                            : Promise.resolve(null),
                    ),
                );
                if (cancelled) return;
                const refined = clips.reduce((acc, c, i) => {
                    const p = probes[i];
                    return acc + (p && p > 0 ? p : fallbackSeconds(c));
                }, 0);
                setSeconds(refined);
            } catch {
                /* keep the sync estimate */
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [clips]);

    // Edge case: < 2 featured clips → hide entirely
    if (clips.length < 2) return null;

    const label = formatDuration(seconds);

    return (
        <>
            <button
                type="button"
                onClick={() => setOpen(true)}
                data-testid="play-featured-reel-button"
                aria-label={`Play featured reel — ${clips.length} clips, about ${label}`}
                className={`inline-flex items-center gap-3 px-4 py-2.5 border border-border hover:border-foreground/60 text-label text-foreground/80 hover:text-foreground transition-colors duration-500 ${className}`}
            >
                <Play
                    size={12}
                    strokeWidth={1.5}
                    className="fill-current"
                    aria-hidden
                />
                <span>Play Featured Reel</span>
                <span
                    aria-hidden
                    className="text-foreground/40 tabular-nums"
                    data-testid="play-featured-reel-duration"
                >
                    · {label}
                </span>
            </button>
            <FeaturedReelPlayer
                clips={clips}
                open={open}
                onClose={() => setOpen(false)}
            />
        </>
    );
};

// Exported for testing / consumers who want the same math.
export {
    DEFAULT_SECONDS,
    fallbackSeconds,
    formatDuration,
    syncEstimate,
    probeVideoDuration,
};

export default PlayFeaturedReelButton;
